const wishlistStorageKey = 'house-of-aarvi-wishlist';
const wishlistChangeEvent = 'house-of-aarvi:wishlist-change';

/**
 * Reads the wishlist from localStorage.
 * Returns an array of {id, handle} objects.
 * Handles both legacy string entries and new object entries.
 */
const readWishlist = () => {
  try {
    const raw = JSON.parse(window.localStorage.getItem(wishlistStorageKey) || '[]');
    if (!Array.isArray(raw)) return [];

    const seen = new Set();
    const result = [];

    for (const entry of raw) {
      let item;
      if (typeof entry === 'string' && entry.length > 0) {
        item = { id: entry, handle: '' };
      } else if (entry && typeof entry === 'object' && typeof entry.id === 'string' && entry.id.length > 0) {
        item = { id: entry.id, handle: entry.handle || '' };
      } else {
        continue;
      }
      if (!seen.has(item.id)) {
        seen.add(item.id);
        result.push(item);
      }
    }

    return result;
  } catch {
    return [];
  }
};

/** Returns a flat array of wishlisted product IDs (convenience helper). */
const readWishlistIds = () => readWishlist().map((item) => item.id);

const writeWishlist = (wishlist) => {
  try {
    window.localStorage.setItem(wishlistStorageKey, JSON.stringify(wishlist));
  } catch {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(wishlistChangeEvent, {
      detail: { wishlist },
    })
  );
};

const syncWishlistUI = () => {
  const wishlist = readWishlist();
  const wishlistIds = new Set(wishlist.map((item) => item.id));

  document.querySelectorAll('[data-wishlist-product-id]').forEach((button) => {
    const productId = button.dataset.wishlistProductId;
    if (!productId) return;

    const isWishlisted = wishlistIds.has(productId);
    const productTitle = button.dataset.wishlistProductTitle || 'product';

    button.setAttribute('aria-pressed', String(isWishlisted));
    button.dataset.wishlistActive = String(isWishlisted);
    button.setAttribute(
      'aria-label',
      `${isWishlisted ? 'Remove' : 'Add'} ${productTitle} ${isWishlisted ? 'from' : 'to'} wishlist`
    );
  });

  document.querySelectorAll('[data-wishlist-count]').forEach((countElement) => {
    countElement.textContent = wishlist.length < 100 ? String(wishlist.length) : '';
    countElement.classList.toggle('hidden', wishlist.length === 0);
  });

  document.querySelectorAll('[data-wishlist-header]').forEach((headerElement) => {
    const itemLabel = wishlist.length === 1 ? 'item' : 'items';
    headerElement.dataset.wishlistActive = String(wishlist.length > 0);
    headerElement.setAttribute(
      'aria-label',
      wishlist.length > 0 ? `Wishlist, ${wishlist.length} saved ${itemLabel}` : 'Wishlist'
    );
  });
};

const handleWishlistClick = (event) => {
  if (!(event.target instanceof Element)) return;

  const wishlistButton = event.target.closest('[data-wishlist-product-id]');
  if (!(wishlistButton instanceof HTMLButtonElement)) return;

  const productId = wishlistButton.dataset.wishlistProductId;
  if (!productId) return;

  event.preventDefault();
  event.stopPropagation();

  const wishlist = readWishlist();
  const exists = wishlist.some((item) => item.id === productId);

  let nextWishlist;
  if (exists) {
    nextWishlist = wishlist.filter((item) => item.id !== productId);
  } else {
    const handle = wishlistButton.dataset.wishlistProductHandle || '';
    nextWishlist = [...wishlist, { id: productId, handle }];
  }

  writeWishlist(nextWishlist);
  syncWishlistUI();
};

/**
 * Legacy migration: resolves product IDs without handles by querying
 * Shopify's storefront search endpoint. Runs once per session if
 * legacy entries are detected. Idempotent — already-resolved entries
 * are skipped.
 */
const migrateLegacyWishlist = async () => {
  const wishlist = readWishlist();
  const legacyEntries = wishlist.filter((item) => !item.handle);

  if (legacyEntries.length === 0) return;

  let changed = false;

  // Resolve handles in parallel (with concurrency limit)
  const resolveHandle = async (entry) => {
    try {
      const res = await fetch(
        `/search/suggest.json?q=id:${entry.id}&resources[type]=product&resources[limit]=1`
      );
      if (!res.ok) return null;

      const data = await res.json();
      const products = data?.resources?.results?.products;

      if (products && products.length > 0) {
        const product = products[0];
        // Verify the ID matches (search is fuzzy, confirm exact match)
        if (String(product.id) === entry.id) {
          return product.handle;
        }
        // Also check with gid format
        if (product.url) {
          const handleFromUrl = product.url.replace(/^\/products\//, '').split('?')[0];
          if (handleFromUrl) return handleFromUrl;
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  // Process in batches of 3 to avoid flooding
  const BATCH_SIZE = 3;
  for (let i = 0; i < legacyEntries.length; i += BATCH_SIZE) {
    const batch = legacyEntries.slice(i, i + BATCH_SIZE);
    const results = await Promise.allSettled(batch.map(resolveHandle));

    results.forEach((result, idx) => {
      if (result.status === 'fulfilled' && result.value) {
        const entry = batch[idx];
        const wishlistEntry = wishlist.find((item) => item.id === entry.id);
        if (wishlistEntry) {
          wishlistEntry.handle = result.value;
          changed = true;
        }
      }
    });
  }

  if (changed) {
    // Remove entries that still have no handle (deleted/unavailable products)
    // Keep them in storage — they just won't render on the wishlist page
    // This preserves the count and the user's intent
    writeWishlist(wishlist);
    syncWishlistUI();
  }
};

const observeWishlistButtons = () => {
  let syncFrame;

  const observer = new MutationObserver(() => {
    if (syncFrame) return;

    syncFrame = requestAnimationFrame(() => {
      syncFrame = undefined;
      syncWishlistUI();
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

const initializeWishlist = () => {
  document.addEventListener('click', handleWishlistClick, true);
  window.addEventListener('storage', (event) => {
    if (event.key === wishlistStorageKey) syncWishlistUI();
  });
  window.addEventListener('pageshow', syncWishlistUI);
  window.addEventListener(wishlistChangeEvent, syncWishlistUI);

  syncWishlistUI();
  observeWishlistButtons();

  // Run legacy migration in background (non-blocking)
  migrateLegacyWishlist();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWishlist, { once: true });
} else {
  initializeWishlist();
}
